import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Circle,
  Clock,
  Download,
  ExternalLink,
  FileStack,
  LifeBuoy,
  Plus,
  Send,
  Star,
} from 'lucide-react';
import { PageHeader, TableSkeleton } from '@/components/shared/page';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, Textarea } from '@/components/ui/misc';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { ApiRequestError } from '@/lib/api';
import { formatBytes, formatDate, formatDateTime, formatMoney, timeAgo } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  downloadPortalDocument,
  useChangePortalPassword,
  useCloseTicket,
  useDecideQuote,
  usePortalContracts,
  usePortalDashboard,
  usePortalDocuments,
  usePortalEntitlement,
  usePortalInvoices,
  usePortalProject,
  usePortalProjects,
  usePortalQuotes,
  usePortalSession,
  usePortalStatement,
  usePortalSubscriptions,
  usePortalTicket,
  usePortalTickets,
  useRaisePortalRequest,
  useRaiseTicket,
  useRateTicket,
  useReplyToTicket,
  useSignOffMilestone,
} from './api';

const TICKET_STATUS_TONE: Record<string, string> = {
  NEW: 'bg-blue-50 text-blue-700 border-blue-200',
  OPEN: 'bg-blue-50 text-blue-700 border-blue-200',
  IN_PROGRESS: 'bg-amber-50 text-amber-800 border-amber-200',
  PENDING_CUSTOMER: 'bg-purple-50 text-purple-700 border-purple-200',
  RESOLVED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  CLOSED: 'bg-slate-100 text-slate-600 border-slate-200',
};

const HEALTH_TONE: Record<string, string> = {
  GREEN: 'text-emerald-600',
  AMBER: 'text-amber-600',
  RED: 'text-red-600',
};

const human = (value: string) => value.replace(/_/g, ' ').toLowerCase();

/** Minutes as hours and minutes. Never a decimal hour. */
function hoursAndMinutes(minutes: number | null): string {
  if (minutes === null) return '—';
  const sign = minutes < 0 ? '-' : '';
  const absolute = Math.abs(minutes);
  return `${sign}${Math.floor(absolute / 60)}h ${absolute % 60}m`;
}

// ── Overview ────────────────────────────────────────────────────────────────

export function PortalDashboardPage() {
  const { data: session } = usePortalSession();
  const { data, isLoading } = usePortalDashboard();

  return (
    <div>
      <PageHeader
        title={`Hello, ${session?.name.split(' ')[0] ?? 'there'}`}
        description={`Everything ${session?.organization.name ?? 'we'} is doing for ${session?.company.name ?? 'you'}.`}
      />

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Tile
          label="Projects in flight"
          value={String(data?.projects.length ?? '—')}
          to="/portal/projects"
        />
        <Tile label="Open tickets" value={String(data?.openTickets ?? '—')} to="/portal/tickets" />
        <Tile
          label="Live contracts"
          value={String(data?.contracts.length ?? '—')}
          to="/portal/services"
        />
        {data?.finance ? (
          <Tile
            label="Outstanding"
            value={formatMoney(data.finance.outstanding)}
            hint={
              Number(data.finance.overdue ?? 0) > 0
                ? `${formatMoney(data.finance.overdue)} overdue`
                : 'Nothing overdue'
            }
            to="/portal/invoices"
            tone={Number(data.finance.overdue ?? 0) > 0 ? 'warning' : undefined}
          />
        ) : (
          <Tile label="Documents" value="View" to="/portal/documents" />
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent tickets</CardTitle>
            <CardDescription>The last few things you raised with us.</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <TableSkeleton rows={3} columns={2} />
            ) : (data?.recentTickets ?? []).length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Nothing raised yet. If something needs attention, tell us.
              </p>
            ) : (
              <div className="space-y-2">
                {data!.recentTickets.map((ticket) => (
                  <Link
                    key={ticket.id}
                    to={`/portal/tickets/${ticket.id}`}
                    className="flex items-start gap-3 rounded-md border p-3 transition-colors hover:bg-muted/50"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{ticket.subject}</p>
                      <p className="text-xs text-muted-foreground">
                        {ticket.number} · raised {timeAgo(ticket.createdAt)}
                      </p>
                    </div>
                    <span
                      className={cn(
                        'shrink-0 rounded-full border px-2 py-0.5 text-[11px]',
                        TICKET_STATUS_TONE[ticket.status] ?? 'border-slate-200',
                      )}
                    >
                      {human(ticket.status)}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Coming up</CardTitle>
            <CardDescription>Milestones on your projects.</CardDescription>
          </CardHeader>
          <CardContent>
            {(data?.upcomingMilestones ?? []).length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Nothing scheduled at the moment.
              </p>
            ) : (
              <div className="space-y-2">
                {data!.upcomingMilestones.map((milestone) => (
                  <div key={milestone.id} className="rounded-md border p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{milestone.name}</p>
                        <p className="text-xs text-muted-foreground">{milestone.project.name}</p>
                      </div>
                      {milestone.plannedEnd && (
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {formatDate(milestone.plannedEnd)}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-primary"
                        style={{ width: `${milestone.percentComplete}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Tile({
  label,
  value,
  hint,
  to,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  to: string;
  tone?: 'warning';
}) {
  return (
    <Link to={to}>
      <Card className="h-full transition-shadow hover:shadow-md">
        <CardContent className="p-5">
          <p className="text-sm font-medium text-muted-foreground">{label}</p>
          <p className={cn('tabular mt-1 text-2xl font-semibold', tone === 'warning' && 'text-amber-700')}>
            {value}
          </p>
          {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
        </CardContent>
      </Card>
    </Link>
  );
}

// ── Projects ────────────────────────────────────────────────────────────────

export function PortalProjectsPage() {
  const { data, isLoading } = usePortalProjects();

  return (
    <div>
      <PageHeader title="Projects" description="What we are building for you, and where it stands." />

      {isLoading ? (
        <TableSkeleton rows={4} columns={4} />
      ) : (data?.data ?? []).length === 0 ? (
        <Card>
          <CardContent className="py-14 text-center text-sm text-muted-foreground">
            No projects yet.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {data!.data.map((project) => (
            <Link key={project.id} to={`/portal/projects/${project.id}`}>
              <Card className="h-full transition-shadow hover:shadow-md">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-medium">{project.name}</p>
                      <p className="text-xs text-muted-foreground">{project.number}</p>
                    </div>
                    <Circle
                      className={cn('h-2.5 w-2.5 shrink-0 fill-current', HEALTH_TONE[project.health])}
                    />
                  </div>

                  {project.description && (
                    <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
                      {project.description}
                    </p>
                  )}

                  <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    <Badge variant="outline">{human(project.status)}</Badge>
                    {project.targetEndDate && <span>due {formatDate(project.targetEndDate)}</span>}
                    {project.manager && <span>with {project.manager.name}</span>}
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function PortalProjectPage() {
  const { id } = useParams<{ id: string }>();
  const { data: project, isLoading } = usePortalProject(id);
  const signOff = useSignOffMilestone(id);
  const [deciding, setDeciding] = useState<{ id: string; name: string } | null>(null);
  const [comment, setComment] = useState('');

  if (isLoading) return <TableSkeleton rows={6} columns={3} />;
  if (!project) return <p className="text-sm text-muted-foreground">Project not found.</p>;

  return (
    <div>
      <Link
        to="/portal/projects"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        All projects
      </Link>

      <PageHeader
        title={project.name}
        description={
          <span className="flex flex-wrap items-center gap-3 text-sm">
            <span>{project.number}</span>
            <Badge variant="outline">{human(project.status)}</Badge>
            {project.manager && <span>Managed by {project.manager.name}</span>}
            {project.targetEndDate && <span>Target {formatDate(project.targetEndDate)}</span>}
          </span>
        }
      />

      {project.description && (
        <Card className="mb-4">
          <CardContent className="p-5 text-sm">{project.description}</CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Milestones</CardTitle>
          <CardDescription>
            Sign off what has been delivered, or send it back with a note.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {(project.milestones ?? []).length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No milestones on this project.
            </p>
          )}

          {(project.milestones ?? []).map((milestone) => (
            <div key={milestone.id} className="rounded-lg border p-4">
              <div className="flex items-start gap-3">
                {milestone.signedOffAt ? (
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                ) : (
                  <Circle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/40" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium">{milestone.name}</p>
                    <Badge variant="outline">{human(milestone.status)}</Badge>
                    {milestone.plannedEnd && (
                      <span className="text-xs text-muted-foreground">
                        due {formatDate(milestone.plannedEnd)}
                      </span>
                    )}
                  </div>

                  {milestone.deliverables && (
                    <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
                      {milestone.deliverables}
                    </p>
                  )}

                  <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-primary"
                      style={{ width: `${milestone.percentComplete}%` }}
                    />
                  </div>

                  {milestone.signedOffAt ? (
                    <p className="mt-2 text-xs text-emerald-700">
                      Signed off by {milestone.signedOffBy} on {formatDate(milestone.signedOffAt)}
                      {milestone.signOffNote && ` — ${milestone.signOffNote}`}
                    </p>
                  ) : milestone.status === 'COMPLETED' ? (
                    <div className="mt-3 flex gap-2">
                      <Button
                        size="sm"
                        onClick={() =>
                          signOff.mutate({ id: milestone.id, approved: true })
                        }
                        disabled={signOff.isPending}
                      >
                        <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                        Sign it off
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDeciding({ id: milestone.id, name: milestone.name })}
                      >
                        Send it back
                      </Button>
                    </div>
                  ) : (
                    <p className="mt-2 text-xs text-muted-foreground">
                      Not ready for sign-off yet.
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {deciding && (
        <Dialog open onOpenChange={() => setDeciding(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Send &ldquo;{deciding.name}&rdquo; back</DialogTitle>
              <DialogDescription>
                It goes back to the team as in progress. Tell them what is missing.
              </DialogDescription>
            </DialogHeader>
            <Textarea
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              rows={4}
              placeholder="What still needs doing"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeciding(null)}>
                Cancel
              </Button>
              <Button
                onClick={() =>
                  signOff.mutate(
                    { id: deciding.id, approved: false, comment },
                    {
                      onSuccess: () => {
                        setDeciding(null);
                        setComment('');
                      },
                    },
                  )
                }
              >
                Send it back
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ── Support ─────────────────────────────────────────────────────────────────

export function PortalTicketsPage() {
  const [filter, setFilter] = useState('open');
  const [raising, setRaising] = useState(false);
  const { data, isLoading } = usePortalTickets(filter);
  const { data: entitlement } = usePortalEntitlement();

  return (
    <div>
      <PageHeader
        title="Support"
        description="Raise something, follow it, and see the time we have promised you."
        actions={
          <div className="flex items-center gap-2">
            <Select value={filter} onChange={(event) => setFilter(event.target.value)} className="w-36">
              <option value="open">Open</option>
              <option value="closed">Resolved</option>
              <option value="">Everything</option>
            </Select>
            <Button onClick={() => setRaising(true)}>
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Raise a ticket
            </Button>
          </div>
        }
      />

      {(entitlement ?? []).length > 0 && (
        <div className="mb-4 grid gap-4 sm:grid-cols-2">
          {entitlement!.map((contract) => (
            <Card key={contract.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{contract.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {contract.number}
                      {contract.slaPolicy && ` · ${contract.slaPolicy}`}
                    </p>
                  </div>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    until {formatDate(contract.endDate)}
                  </span>
                </div>

                {contract.allocatedMinutes !== null && (
                  <div className="mt-3">
                    <div className="flex items-baseline justify-between text-sm">
                      <span className="tabular font-medium">
                        {hoursAndMinutes(contract.availableMinutes)} left
                      </span>
                      <span className="text-xs text-muted-foreground">
                        of {hoursAndMinutes(contract.allocatedMinutes)}
                      </span>
                    </div>
                    <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                      <div
                        className={cn(
                          'h-full rounded-full',
                          (contract.availableMinutes ?? 0) <= 0 ? 'bg-red-500' : 'bg-primary',
                        )}
                        style={{
                          width: `${Math.max(
                            0,
                            Math.min(
                              100,
                              ((contract.availableMinutes ?? 0) / (contract.allocatedMinutes || 1)) * 100,
                            ),
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6">
              <TableSkeleton rows={5} columns={4} />
            </div>
          ) : (data?.data ?? []).length === 0 ? (
            <div className="py-14 text-center">
              <LifeBuoy className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
              <p className="font-medium">Nothing here</p>
              <p className="mt-1 text-sm text-muted-foreground">
                If something needs our attention, raise it and we will pick it up.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ticket</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>With</TableHead>
                  <TableHead>Raised</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data!.data.map((ticket) => (
                  <TicketRow key={ticket.id} ticket={ticket} />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {raising && <RaiseTicketDialog onClose={() => setRaising(false)} />}
    </div>
  );
}

function TicketRow({ ticket }: { ticket: { id: string; number: string; subject: string; status: string; priority: string; createdAt: string; resolutionDueAt: string | null; assignee: { name: string } | null } }) {
  const navigate = useNavigate();
  const overdue =
    ticket.resolutionDueAt &&
    new Date(ticket.resolutionDueAt) < new Date() &&
    !['RESOLVED', 'CLOSED'].includes(ticket.status);

  return (
    <TableRow className="cursor-pointer" onClick={() => navigate(`/portal/tickets/${ticket.id}`)}>
      <TableCell>
        <p className="text-sm font-medium">{ticket.subject}</p>
        <p className="text-xs text-muted-foreground">
          {ticket.number} · {human(ticket.priority)}
        </p>
      </TableCell>
      <TableCell>
        <span
          className={cn(
            'rounded-full border px-2 py-0.5 text-[11px]',
            TICKET_STATUS_TONE[ticket.status] ?? 'border-slate-200',
          )}
        >
          {human(ticket.status)}
        </span>
        {overdue && (
          <span className="ml-2 inline-flex items-center gap-1 text-xs text-amber-700">
            <AlertTriangle className="h-3 w-3" />
            past target
          </span>
        )}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {ticket.assignee?.name ?? 'Being assigned'}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">{timeAgo(ticket.createdAt)}</TableCell>
    </TableRow>
  );
}

export function PortalTicketPage() {
  const { id } = useParams<{ id: string }>();
  const { data: ticket, isLoading } = usePortalTicket(id);
  const reply = useReplyToTicket(id);
  const close = useCloseTicket(id);
  const rate = useRateTicket(id);

  const [draft, setDraft] = useState('');
  const [rating, setRating] = useState(0);

  if (isLoading) return <TableSkeleton rows={5} columns={2} />;
  if (!ticket) return <p className="text-sm text-muted-foreground">Ticket not found.</p>;

  const closed = ['RESOLVED', 'CLOSED'].includes(ticket.status);

  return (
    <div>
      <Link
        to="/portal/tickets"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        All tickets
      </Link>

      <PageHeader
        title={ticket.subject}
        description={
          <span className="flex flex-wrap items-center gap-3 text-sm">
            <span>{ticket.number}</span>
            <span
              className={cn(
                'rounded-full border px-2 py-0.5 text-[11px]',
                TICKET_STATUS_TONE[ticket.status] ?? 'border-slate-200',
              )}
            >
              {human(ticket.status)}
            </span>
            {ticket.assignee && <span>with {ticket.assignee.name}</span>}
            {ticket.resolutionDueAt && !closed && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                target {formatDateTime(ticket.resolutionDueAt)}
              </span>
            )}
          </span>
        }
        actions={
          !closed ? (
            <Button variant="outline" onClick={() => close.mutate()}>
              Close it — this is sorted
            </Button>
          ) : undefined
        }
      />

      <Card className="mb-4">
        <CardContent className="space-y-4 p-5">
          {(ticket.messages ?? []).map((message) => (
            <div
              key={message.id}
              className={cn(
                'rounded-lg px-4 py-3',
                message.fromClient ? 'ml-8 bg-primary/5' : 'mr-8 bg-muted/50',
              )}
            >
              <div className="mb-1 flex items-baseline gap-2">
                <span className="text-sm font-medium">
                  {message.fromClient ? 'You' : (message.author?.name ?? 'Support')}
                </span>
                <span className="text-xs text-muted-foreground">{timeAgo(message.createdAt)}</span>
              </div>
              <p className="whitespace-pre-wrap text-sm">{message.body}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      {!closed ? (
        <Card>
          <CardContent className="p-4">
            <Textarea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              rows={3}
              placeholder="Add to this ticket…"
            />
            {reply.isError && (
              <div className="mt-2">
                <FormError message={(reply.error as ApiRequestError).message} />
              </div>
            )}
            <div className="mt-2 flex justify-end">
              <Button
                disabled={!draft.trim() || reply.isPending}
                onClick={() => reply.mutate(draft, { onSuccess: () => setDraft('') })}
              >
                <Send className="mr-1.5 h-3.5 w-3.5" />
                Send
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : ticket.csatScore === null ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">How did we do?</CardTitle>
            <CardDescription>One rating, and it helps more than you would think.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((score) => (
                <button
                  key={score}
                  onClick={() => {
                    setRating(score);
                    rate.mutate({ rating: score });
                  }}
                  className="p-1"
                  aria-label={`${score} out of 5`}
                >
                  <Star
                    className={cn(
                      'h-6 w-6 transition-colors',
                      score <= rating
                        ? 'fill-amber-400 text-amber-400'
                        : 'text-muted-foreground/30 hover:text-amber-400',
                    )}
                  />
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="flex items-center gap-2 p-5 text-sm">
            <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
            You rated this {ticket.csatScore} out of 5. Thank you.
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function RaiseTicketDialog({ onClose }: { onClose: () => void }) {
  const raise = useRaiseTicket();
  const navigate = useNavigate();
  const { data: projects } = usePortalProjects();

  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('NORMAL');
  const [projectId, setProjectId] = useState('');

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Raise a ticket</DialogTitle>
          <DialogDescription>
            Tell us what is happening. The clock on our response starts the moment you send it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="ticket-subject">What is it about?</Label>
            <Input
              id="ticket-subject"
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              placeholder="Reports are not loading on the dashboard"
            />
          </div>
          <div>
            <Label htmlFor="ticket-description">What is happening?</Label>
            <Textarea
              id="ticket-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={5}
              placeholder="What you did, what you expected, and what happened instead."
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ticket-priority">How urgent?</Label>
              <Select
                id="ticket-priority"
                value={priority}
                onChange={(event) => setPriority(event.target.value)}
              >
                <option value="LOW">Low — whenever</option>
                <option value="NORMAL">Normal</option>
                <option value="HIGH">High — it is blocking us</option>
                <option value="URGENT">Urgent — everything is stopped</option>
              </Select>
            </div>
            <div>
              <Label htmlFor="ticket-project">Related project</Label>
              <Select
                id="ticket-project"
                value={projectId}
                onChange={(event) => setProjectId(event.target.value)}
              >
                <option value="">Not project-specific</option>
                {(projects?.data ?? []).map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {raise.isError && <FormError message={(raise.error as ApiRequestError).message} />}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!subject.trim() || !description.trim() || raise.isPending}
            onClick={() =>
              raise.mutate(
                {
                  subject,
                  description,
                  priority,
                  ...(projectId ? { projectId } : {}),
                },
                {
                  onSuccess: (ticket) => {
                    onClose();
                    navigate(`/portal/tickets/${ticket.id}`);
                  },
                },
              )
            }
          >
            Raise it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Money ───────────────────────────────────────────────────────────────────

export function PortalInvoicesPage() {
  const [filter, setFilter] = useState('');
  const { data, isLoading } = usePortalInvoices(filter);
  const { data: statement } = usePortalStatement();

  return (
    <div>
      <PageHeader
        title="Invoices"
        description="What has been billed, what is paid, and what is outstanding."
        actions={
          <Select value={filter} onChange={(event) => setFilter(event.target.value)} className="w-36">
            <option value="">All invoices</option>
            <option value="unpaid">Outstanding</option>
            <option value="paid">Paid</option>
          </Select>
        }
      />

      {statement && (
        <Card className="mb-4">
          <CardContent className="flex flex-wrap gap-8 p-5">
            <div>
              <p className="text-xs text-muted-foreground">Opening balance</p>
              <p className="tabular text-lg font-semibold">{formatMoney(statement.opening)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Closing balance</p>
              <p className="tabular text-lg font-semibold">{formatMoney(statement.closing)}</p>
            </div>
            <p className="ml-auto self-end text-xs text-muted-foreground">
              {formatDate(statement.from)} to {formatDate(statement.to)}
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6">
              <TableSkeleton rows={5} columns={5} />
            </div>
          ) : (data?.data ?? []).length === 0 ? (
            <p className="py-14 text-center text-sm text-muted-foreground">Nothing to show.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Issued</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data!.data.map((invoice) => {
                  const overdue =
                    Number(invoice.balanceDue ?? 0) > 0 && new Date(invoice.dueDate) < new Date();
                  return (
                    <TableRow key={invoice.id}>
                      <TableCell>
                        <p className="text-sm font-medium">{invoice.number}</p>
                        {invoice.poReference && (
                          <p className="text-xs text-muted-foreground">PO {invoice.poReference}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{formatDate(invoice.issueDate)}</TableCell>
                      <TableCell className={cn('text-sm', overdue && 'text-red-700')}>
                        {formatDate(invoice.dueDate)}
                      </TableCell>
                      <TableCell className="tabular text-right">
                        {formatMoney(invoice.grandTotal, invoice.currency)}
                      </TableCell>
                      <TableCell
                        className={cn('tabular text-right font-medium', overdue && 'text-red-700')}
                      >
                        {formatMoney(invoice.balanceDue, invoice.currency)}
                      </TableCell>
                      <TableCell className="text-right">
                        <a
                          href={`/i/${invoice.publicToken}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                        >
                          Open
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {statement && statement.lines.length > 0 && (
        <Card className="mt-4">
          <CardHeader>
            <CardTitle className="text-base">Statement of account</CardTitle>
            <CardDescription>Everything billed and everything received, in order.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Detail</TableHead>
                    <TableHead className="text-right">Charged</TableHead>
                    <TableHead className="text-right">Received</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {statement.lines.map((line) => (
                    <TableRow key={`${line.kind}-${line.id}`}>
                      <TableCell className="text-sm">{formatDate(line.date)}</TableCell>
                      <TableCell className="text-sm font-medium">{line.reference}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{line.detail}</TableCell>
                      <TableCell className="tabular text-right">
                        {Number(line.debit ?? 0) > 0 ? formatMoney(line.debit) : ''}
                      </TableCell>
                      <TableCell className="tabular text-right text-emerald-700">
                        {Number(line.credit ?? 0) > 0 ? formatMoney(line.credit) : ''}
                      </TableCell>
                      <TableCell className="tabular text-right font-medium">
                        {formatMoney(line.balance)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export function PortalQuotesPage() {
  const { data, isLoading } = usePortalQuotes();
  const decide = useDecideQuote();

  return (
    <div>
      <PageHeader title="Quotes" description="What we have proposed, and where each one stands." />

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6">
              <TableSkeleton rows={3} columns={4} />
            </div>
          ) : (data ?? []).length === 0 ? (
            <p className="py-14 text-center text-sm text-muted-foreground">No open quotes.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Quote</TableHead>
                  <TableHead>Issued</TableHead>
                  <TableHead>Valid until</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data!.map((quote) => (
                  <TableRow key={quote.id}>
                    <TableCell className="text-sm font-medium">
                      {quote.number}
                      {quote.version > 1 && (
                        <span className="ml-1 text-xs text-muted-foreground">v{quote.version}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{formatDate(quote.issueDate)}</TableCell>
                    <TableCell className="text-sm">
                      {quote.validUntil ? formatDate(quote.validUntil) : '—'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{human(quote.status)}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <a
                          href={`/q/${quote.publicToken}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                        >
                          Read it
                          <ExternalLink className="h-3 w-3" />
                        </a>
                        {['SENT', 'VIEWED'].includes(quote.status) && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => decide.mutate({ id: quote.id, accept: true })}
                              disabled={decide.isPending}
                            >
                              Accept
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => decide.mutate({ id: quote.id, accept: false })}
                              disabled={decide.isPending}
                            >
                              Decline
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Services and documents ──────────────────────────────────────────────────

export function PortalServicesPage() {
  const { data: contracts } = usePortalContracts();
  const { data: subscriptions } = usePortalSubscriptions();
  const [requesting, setRequesting] = useState<{ type: string; id: string; label: string } | null>(null);

  return (
    <div>
      <PageHeader
        title="Services"
        description="Your subscriptions and support agreements. Ask for a change and it reaches your account manager."
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Subscriptions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(subscriptions ?? []).length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground">None active.</p>
            )}
            {(subscriptions ?? []).map((subscription) => (
              <div key={subscription.id} className="rounded-lg border p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{subscription.product.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {subscription.plan.name} · {subscription.quantity}{' '}
                      {subscription.quantity === 1 ? 'seat' : 'seats'}
                    </p>
                  </div>
                  <Badge variant="outline">{human(subscription.status)}</Badge>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                  <span>renews {formatDate(subscription.currentPeriodEnd)}</span>
                  {subscription.unitPrice && (
                    <span className="tabular">
                      {formatMoney(subscription.unitPrice, subscription.currency)} per{' '}
                      {human(subscription.billingCycle)}
                    </span>
                  )}
                  {subscription.instanceUrl && (
                    <a
                      href={subscription.instanceUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      Open it
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>

                <Button
                  size="sm"
                  variant="outline"
                  className="mt-3"
                  onClick={() =>
                    setRequesting({
                      type: 'subscription',
                      id: subscription.id,
                      label: subscription.product.name,
                    })
                  }
                >
                  Request a change
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Support agreements</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(contracts ?? []).length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground">None in place.</p>
            )}
            {(contracts ?? []).map((contract) => (
              <div key={contract.id} className="rounded-lg border p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{contract.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {contract.number} · {human(contract.contractType)}
                    </p>
                  </div>
                  <Badge variant="outline">{human(contract.status)}</Badge>
                </div>

                <p className="mt-2 text-xs text-muted-foreground">
                  {formatDate(contract.startDate)} to {formatDate(contract.endDate)}
                  {contract.slaPolicy && ` · ${contract.slaPolicy.name}`}
                </p>

                {contract.coverageIncluded && (
                  <p className="mt-2 whitespace-pre-wrap text-sm">{contract.coverageIncluded}</p>
                )}

                <Button
                  size="sm"
                  variant="outline"
                  className="mt-3"
                  onClick={() =>
                    setRequesting({ type: 'contract', id: contract.id, label: contract.name })
                  }
                >
                  Request a change
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {requesting && <RequestDialog target={requesting} onClose={() => setRequesting(null)} />}
    </div>
  );
}

function RequestDialog({
  target,
  onClose,
}: {
  target: { type: string; id: string; label: string };
  onClose: () => void;
}) {
  const raise = useRaisePortalRequest();
  const [requestType, setRequestType] = useState('change');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request a change to {target.label}</DialogTitle>
          <DialogDescription>
            This reaches your account manager. They will come back to you.
          </DialogDescription>
        </DialogHeader>

        {raise.isSuccess ? (
          <p className="rounded-md bg-emerald-50 px-3 py-2.5 text-sm text-emerald-900">
            Sent. Somebody will be in touch.
          </p>
        ) : (
          <div className="space-y-3">
            <div>
              <Label htmlFor="request-type">What kind of change?</Label>
              <Select
                id="request-type"
                value={requestType}
                onChange={(event) => setRequestType(event.target.value)}
              >
                <option value="upgrade">Upgrade or add seats</option>
                <option value="change">Change something</option>
                <option value="callback">Ask for a call</option>
                <option value="other">Something else</option>
              </Select>
            </div>
            <div>
              <Label htmlFor="request-subject">In one line</Label>
              <Input
                id="request-subject"
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                placeholder="We need five more seats from October"
              />
            </div>
            <div>
              <Label htmlFor="request-body">Detail</Label>
              <Textarea
                id="request-body"
                value={body}
                onChange={(event) => setBody(event.target.value)}
                rows={4}
              />
            </div>
            {raise.isError && <FormError message={(raise.error as ApiRequestError).message} />}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {raise.isSuccess ? 'Done' : 'Cancel'}
          </Button>
          {!raise.isSuccess && (
            <Button
              disabled={!subject.trim() || !body.trim() || raise.isPending}
              onClick={() =>
                raise.mutate({
                  requestType,
                  subject,
                  body,
                  entityType: target.type,
                  entityId: target.id,
                })
              }
            >
              Send it
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PortalDocumentsPage() {
  const { data, isLoading } = usePortalDocuments();
  const [downloading, setDownloading] = useState<string | null>(null);

  return (
    <div>
      <PageHeader
        title="Documents"
        description="Contracts, reports and anything else shared with you. Every download is recorded."
      />

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6">
              <TableSkeleton rows={4} columns={3} />
            </div>
          ) : (data ?? []).length === 0 ? (
            <div className="py-14 text-center">
              <FileStack className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
              <p className="font-medium">Nothing shared yet</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Documents your team shares with you will appear here.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Document</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data!.map((document) => (
                  <TableRow key={document.id}>
                    <TableCell>
                      <p className="text-sm font-medium">{document.name}</p>
                      {document.description && (
                        <p className="text-xs text-muted-foreground">{document.description}</p>
                      )}
                      {document.currentVersion && (
                        <p className="text-xs text-muted-foreground">
                          v{document.currentVersion.version} ·{' '}
                          {formatBytes(Number(document.currentVersion.sizeBytes))}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {human(document.category)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(document.updatedAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={downloading === document.id}
                        onClick={async () => {
                          setDownloading(document.id);
                          try {
                            await downloadPortalDocument(document.id);
                          } finally {
                            setDownloading(null);
                          }
                        }}
                      >
                        <Download className="mr-1.5 h-3.5 w-3.5" />
                        Download
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function PortalAccountPage() {
  const { data: session } = usePortalSession();
  const change = useChangePortalPassword();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  return (
    <div>
      <PageHeader title="Your account" description={session?.email} />

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="text-base">Change your password</CardTitle>
          <CardDescription>Changing it signs out every other session.</CardDescription>
        </CardHeader>
        <CardContent>
          {change.isSuccess ? (
            <p className="rounded-md bg-emerald-50 px-3 py-2.5 text-sm text-emerald-900">
              Password changed.
            </p>
          ) : (
            <form
              className="space-y-3"
              onSubmit={(event) => {
                event.preventDefault();
                change.mutate({ currentPassword, newPassword });
              }}
            >
              <div>
                <Label htmlFor="current-password">Current password</Label>
                <Input
                  id="current-password"
                  type="password"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(event) => setCurrentPassword(event.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="new-password">New password</Label>
                <Input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  required
                />
              </div>
              {change.isError && <FormError message={(change.error as ApiRequestError).message} />}
              <Button type="submit" disabled={change.isPending}>
                Change it
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
